import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Cookie, X, Check, Shield } from "lucide-react";

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('freestays_cookie_consent');
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('freestays_cookie_consent', 'accepted');
    localStorage.setItem('freestays_cookie_consent_at', new Date().toISOString());
    setShowBanner(false);
  };

  const handleDeny = () => {
    localStorage.setItem('freestays_cookie_consent', 'denied');
    localStorage.setItem('freestays_cookie_consent_at', new Date().toISOString());
    setShowBanner(false);
  };

  const handleClose = () => {
    // Soft-dismiss: not persisted, will return on next page load
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Soft scrim on mobile so the banner sits visually anchored, but no
          pointer-events so the rest of the page stays interactive */}
      <div
        className="fixed inset-x-0 bottom-0 h-40 sm:h-32 pointer-events-none z-[55]
                   bg-gradient-to-t from-black/30 via-black/10 to-transparent
                   sm:from-black/15 sm:via-transparent
                   animate-fadeIn"
        aria-hidden="true"
      />

      <div
        className="fixed left-0 right-0 z-[60] animate-slideInUp px-3 sm:px-4
                   pb-[max(env(safe-area-inset-bottom),0.75rem)]
                   bottom-0 sm:bottom-3"
        style={{ pointerEvents: 'none' }}
        data-testid="cookie-consent"
      >
        <div
          className="mx-auto w-full sm:max-w-3xl rounded-2xl sm:rounded-3xl shadow-2xl
                     border border-white/10 dark:border-white/10
                     bg-white/95 dark:bg-[#081D33]/95 backdrop-blur-xl
                     ring-1 ring-black/5
                     overflow-hidden"
          style={{ pointerEvents: 'auto' }}
        >
          {/* Subtle top accent bar — uses brand orange */}
          <div className="h-[3px] bg-gradient-to-r from-[#FFAF00] via-[#FF7A00] to-[#FFAF00]" />

          <div className="px-4 sm:px-5 py-3.5 sm:py-3">
            <div className="flex items-start gap-3 sm:items-center sm:gap-4">
              {/* Icon */}
              <div className="flex-shrink-0 hidden sm:flex w-9 h-9 rounded-xl bg-[#00A9E0]/10 dark:bg-[#00A9E0]/15 items-center justify-center">
                <Cookie className="w-4.5 h-4.5 text-[#00A9E0]" />
              </div>

              {/* Message */}
              <div className="flex-1 min-w-0">
                <p className="text-[13px] sm:text-sm font-medium leading-snug text-foreground">
                  <Cookie className="w-4 h-4 text-[#00A9E0] inline-block mr-1.5 sm:hidden align-text-bottom" />
                  We use cookies to enhance your experience.
                </p>
                <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5 leading-snug">
                  By continuing you agree to our cookie policy.{" "}
                  <a
                    href="/privacy-policy"
                    className="underline underline-offset-2 hover:text-foreground"
                  >
                    Read more
                  </a>
                </p>
              </div>

              {/* Buttons (desktop inline, mobile = next row) */}
              <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDeny}
                  className="rounded-full px-4 text-xs text-muted-foreground hover:text-foreground"
                  data-testid="cookie-deny"
                >
                  Deny
                </Button>
                <Button
                  size="sm"
                  onClick={handleAccept}
                  className="rounded-full px-5 text-xs font-semibold bg-gradient-to-b from-[#FFAF00] to-[#FF7A00] hover:brightness-110 text-white shadow-md border-0"
                  data-testid="cookie-accept"
                >
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                  Accept all
                </Button>
              </div>

              {/* Close button — soft dismiss without persistence */}
              <button
                onClick={handleClose}
                className="flex-shrink-0 w-7 h-7 rounded-full hover:bg-foreground/5 inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition"
                aria-label="Close"
                data-testid="cookie-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Mobile-only buttons row */}
            <div className="flex sm:hidden items-center gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeny}
                className="flex-1 rounded-full text-xs"
                data-testid="cookie-deny-mobile"
              >
                Deny
              </Button>
              <Button
                size="sm"
                onClick={handleAccept}
                className="flex-1 rounded-full text-xs font-semibold bg-gradient-to-b from-[#FFAF00] to-[#FF7A00] hover:brightness-110 text-white shadow-md border-0"
                data-testid="cookie-accept-mobile"
              >
                <Check className="w-3.5 h-3.5 mr-1.5" />
                Accept all
              </Button>
            </div>

            {/* Trust badge tiny line */}
            <div className="hidden sm:flex items-center gap-1.5 mt-1.5 text-[10px] text-muted-foreground/70">
              <Shield className="w-3 h-3" />
              GDPR-compliant — your choice is stored locally only.
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
